/**
 * Reflections — private encrypted journal with optional passcode protection.
 *
 * Server encrypts every entry's content with Fernet at rest; this page only
 * ever sees plaintext over the authenticated API. When a passcode is set,
 * the list view shows only timestamps and word counts until the user enters
 * the correct passcode. The passcode unlock resets when the user navigates
 * away from this page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import usePersistentViewMode from '../hooks/usePersistentViewMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Auto-lock the in-memory passcode after this many ms of tab invisibility.
const IDLE_LOCK_MS = 5 * 60 * 1000;

interface ReflectionListItem {
  id: number;
  user_id: number;
  title?: string | null;
  preview?: string | null;
  word_count: number;
  reflected_at: string;
  created_at: string;
  updated_at?: string | null;
}

interface ReflectionDetail {
  id: number;
  user_id: number;
  title?: string | null;
  content: string;
  word_count: number;
  reflected_at: string;
  created_at: string;
  updated_at?: string | null;
}

type EditorMode = 'create' | 'edit';

const countWords = (text: string): number => {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

/** Milliseconds at 00:00:00 UTC for the current UTC date. Matches backend `_today_utc_midnight` / `_sum_words_today`. */
function utcCalendarDayStartMs(): number {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

/** Convert a Date to a local datetime-local input value (YYYY-MM-DDTHH:MM). */
const toDatetimeLocal = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Reflections: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
  } = useLayoutTokens();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('reflections');
  const [reflections, setReflections] = useState<ReflectionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Passcode state
  const [passcodeIsSet, setPasscodeIsSet] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passcode, setPasscode] = useState(''); // held in memory for API calls while unlocked
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPasscodeInput, setUnlockPasscodeInput] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  // Passcode settings
  const [passcodeSettingsOpen, setPasscodeSettingsOpen] = useState(false);
  const [newPasscodeInput, setNewPasscodeInput] = useState('');
  const [passcodeSettingsLoading, setPasscodeSettingsLoading] = useState(false);
  const [removePasscodeInput, setRemovePasscodeInput] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [contentDraft, setContentDraft] = useState('');
  const [reflectedAtDraft, setReflectedAtDraft] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewing, setViewing] = useState<ReflectionDetail | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ReflectionListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const locked = passcodeIsSet && !isUnlocked;

  /** Build headers for passcode-gated requests. */
  const passcodeHeaders = useCallback(() => {
    if (passcodeIsSet && passcode) {
      return { 'X-Reflection-Passcode': passcode };
    }
    return {};
  }, [passcodeIsSet, passcode]);

  const fetchPasscodeStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/reflections/passcode/status`);
      setPasscodeIsSet(res.data.is_set);
    } catch {
      // Non-critical — assume no passcode.
    }
  };

  const fetchReflections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/reflections/`, {
        headers: passcodeHeaders(),
      });
      setReflections(response.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load reflections'));
    } finally {
      setLoading(false);
    }
  }, [passcodeHeaders]);

  useEffect(() => {
    const init = async () => {
      await fetchPasscodeStatus();
      await fetchReflections();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when unlock state changes.
  useEffect(() => {
    if (!loading) {
      fetchReflections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnlocked]);

  /** Auto-lock after the tab loses visibility for ``IDLE_LOCK_MS`` (default
   * 5 minutes). The plaintext passcode lives only in component state, so
   * locking it clears the in-memory copy and forces re-verification on
   * the next read. Backed up by a ``visibilitychange`` immediate-lock for
   * users who switch away from the tab altogether. */
  useEffect(() => {
    if (!passcodeIsSet || !isUnlocked) return;
    let hideTimer: number | null = null;

    const lockNow = () => {
      setIsUnlocked(false);
      setPasscode('');
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Start (or reset) the idle timer when the tab loses focus.
        if (hideTimer !== null) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(lockNow, IDLE_LOCK_MS);
      } else {
        // User came back before the timer fired — cancel pending lock.
        if (hideTimer !== null) {
          window.clearTimeout(hideTimer);
          hideTimer = null;
        }
      }
    };

    // Defensive: clear the in-memory passcode on unload so it never lives
    // beyond the active tab session.
    const onBeforeUnload = () => lockNow();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      if (hideTimer !== null) window.clearTimeout(hideTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [passcodeIsSet, isUnlocked]);

  const draftWordCount = useMemo(() => countWords(contentDraft), [contentDraft]);

  const totalWordsToday = useMemo(() => {
    const startMs = utcCalendarDayStartMs();
    return reflections
      .filter((r) => new Date(r.created_at).getTime() >= startMs)
      .reduce((sum, r) => sum + (r.word_count || 0), 0);
  }, [reflections]);

  // --- Passcode unlock ---

  const handleUnlock = async () => {
    if (!unlockPasscodeInput.trim()) return;
    try {
      setUnlockLoading(true);
      setUnlockError('');
      await axios.post(`${API_BASE_URL}/api/reflections/passcode/verify`, {
        passcode: unlockPasscodeInput,
      });
      setPasscode(unlockPasscodeInput);
      setIsUnlocked(true);
      setUnlockDialogOpen(false);
      setUnlockPasscodeInput('');
    } catch (err: any) {
      setUnlockError(
        err?.response?.status === 403
          ? 'Incorrect passcode.'
          : getApiErrorMessage(err, 'Verification failed'),
      );
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    setPasscode('');
  };

  // --- Passcode settings ---

  const handleSetPasscode = async () => {
    if (newPasscodeInput.length < 4) {
      setError('Passcode must be at least 4 characters.');
      return;
    }
    try {
      setPasscodeSettingsLoading(true);
      await axios.post(`${API_BASE_URL}/api/reflections/passcode`, {
        passcode: newPasscodeInput,
      });
      setPasscodeIsSet(true);
      setPasscode(newPasscodeInput);
      setIsUnlocked(true);
      setNewPasscodeInput('');
      setPasscodeSettingsOpen(false);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to set passcode'));
    } finally {
      setPasscodeSettingsLoading(false);
    }
  };

  const handleRemovePasscode = async () => {
    if (!removePasscodeInput.trim()) return;
    try {
      setPasscodeSettingsLoading(true);
      await axios.delete(`${API_BASE_URL}/api/reflections/passcode`, {
        data: { passcode: removePasscodeInput },
      });
      setPasscodeIsSet(false);
      setIsUnlocked(false);
      setPasscode('');
      setRemovePasscodeInput('');
      setPasscodeSettingsOpen(false);
      await fetchReflections();
    } catch (err: any) {
      setError(
        err?.response?.status === 403
          ? 'Incorrect passcode.'
          : getApiErrorMessage(err, 'Failed to remove passcode'),
      );
    } finally {
      setPasscodeSettingsLoading(false);
    }
  };

  // --- CRUD ---

  const openCreate = () => {
    setEditorMode('create');
    setEditingId(null);
    setTitleDraft('');
    setContentDraft('');
    setReflectedAtDraft(toDatetimeLocal(new Date()));
    setEditorOpen(true);
  };

  const openEdit = async (item: ReflectionListItem) => {
    try {
      setViewerLoading(true);
      const response = await axios.get<ReflectionDetail>(
        `${API_BASE_URL}/api/reflections/${item.id}`,
        { headers: passcodeHeaders() },
      );
      setEditorMode('edit');
      setEditingId(item.id);
      setTitleDraft(response.data.title || '');
      setContentDraft(response.data.content);
      setReflectedAtDraft(toDatetimeLocal(new Date(response.data.reflected_at)));
      setEditorOpen(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to open reflection for editing'));
    } finally {
      setViewerLoading(false);
    }
  };

  const openViewer = async (item: ReflectionListItem) => {
    if (locked) {
      setUnlockDialogOpen(true);
      return;
    }
    try {
      setViewerLoading(true);
      const response = await axios.get<ReflectionDetail>(
        `${API_BASE_URL}/api/reflections/${item.id}`,
        { headers: passcodeHeaders() },
      );
      setViewing(response.data);
      setViewerOpen(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load reflection'));
    } finally {
      setViewerLoading(false);
    }
  };

  const handleSave = async () => {
    const trimmed = contentDraft.trim();
    if (!trimmed) {
      setError('Reflection content cannot be empty');
      return;
    }
    try {
      setSaveLoading(true);
      const reflected_at = reflectedAtDraft
        ? new Date(reflectedAtDraft).toISOString()
        : undefined;
      const payload: any = {
        title: titleDraft.trim() || null,
        content: contentDraft,
        reflected_at,
      };
      if (editorMode === 'create') {
        await axios.post(`${API_BASE_URL}/api/reflections/`, payload);
      } else if (editingId != null) {
        await axios.put(`${API_BASE_URL}/api/reflections/${editingId}`, payload, {
          headers: passcodeHeaders(),
        });
      }
      setEditorOpen(false);
      await fetchReflections();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save reflection'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`${API_BASE_URL}/api/reflections/${deleteTarget.id}`, {
        headers: passcodeHeaders(),
      });
      setDeleteTarget(null);
      await fetchReflections();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete reflection'));
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <PageShell maxWidth="wide">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3" component="h1">
                Reflections
              </Typography>
              <Tooltip title={locked ? 'Locked — enter passcode to view entries' : 'Your entries are encrypted at rest with a server-side key.'}>
                {locked ? <LockIcon color="warning" /> : <LockIcon color="primary" />}
              </Tooltip>
              {passcodeIsSet && isUnlocked && (
                <Tooltip title="Lock reflections">
                  <IconButton size="small" onClick={handleLock}>
                    <LockOpenIcon color="success" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Passcode settings">
                <IconButton size="small" onClick={() => setPasscodeSettingsOpen(true)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              A private, encrypted journal. Paste voice-transcribed notes from your phone or
              type freely — everything is encrypted before it's stored.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignSelf={{ xs: 'stretch', sm: 'center' }}
            justifyContent="flex-end"
          >
            <ViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              ariaLabel="reflection view mode"
              isOverride={viewModeControls.isOverride}
              onUseDefault={viewModeControls.useDefault}
            />
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              New Reflection
            </Button>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`${reflections.length} ${reflections.length === 1 ? 'entry' : 'entries'}`}
            size="small"
          />
          <Chip
            label={`${totalWordsToday.toLocaleString()} words today`}
            color="primary"
            size="small"
          />
          {locked && (
            <Chip
              icon={<LockIcon />}
              label="Locked — tap to unlock"
              color="warning"
              size="small"
              onClick={() => setUnlockDialogOpen(true)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reflections.length === 0 ? (
        <Card>
          {/* The trailing py deliberately outranks the token's vertical axis:
              an empty state is meant to be airy, and it takes its horizontal
              padding from the token. Intentional override, not an sx-order slip. */}
          <CardContent sx={[cardContentPaddingSx, { textAlign: 'center', py: { xs: 4, md: 5 } }]}>
            <Typography variant="h6" gutterBottom>
              No reflections yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start writing. Your words will be encrypted the moment they leave the browser.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Write Your First Reflection
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {reflections.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => openViewer(item)} sx={{ flexGrow: 1 }}>
                  <CardContent sx={cardContentPaddingSx}>
                    {locked ? (
                      <>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.disabled' }} noWrap>
                          Locked entry
                        </Typography>
                        <Box
                          sx={{
                            minHeight: '5.5em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <LockIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }} noWrap>
                          {item.title?.trim() || 'Untitled reflection'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '5.5em',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {item.preview || '(empty)'}
                        </Typography>
                      </>
                    )}
                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                      <Chip label={`${item.word_count} words`} size="small" />
                      <Chip label={formatDate(item.reflected_at)} size="small" variant="outlined" />
                    </Stack>
                  </CardContent>
                </CardActionArea>
                {!locked && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      px: 1,
                      pb: 1,
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(item);
                        }}
                        aria-label={`Edit ${item.title || 'reflection'}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(item);
                        }}
                        aria-label={`Delete ${item.title || 'reflection'}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {reflections.map((item) => {
            const displayTitle = locked
              ? 'Locked entry'
              : item.title?.trim() || 'Untitled reflection';
            const openFromKeyboard = (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openViewer(item);
              }
            };

            return (
              <Paper key={item.id} sx={panelPaddingSx}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: locked
                        ? 'minmax(0, 1.4fr) minmax(180px, 0.8fr) auto'
                        : 'minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(180px, 0.8fr) auto',
                    },
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => openViewer(item)}
                    onKeyDown={openFromKeyboard}
                    sx={{ minWidth: 0, cursor: 'pointer' }}
                    aria-label={locked ? 'Unlock reflection entry' : `Open ${displayTitle}`}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {locked && <LockIcon color="warning" fontSize="small" />}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: locked ? 'text.disabled' : 'text.primary',
                        }}
                        noWrap
                      >
                        {displayTitle}
                      </Typography>
                    </Stack>
                  </Box>

                  {!locked && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {item.preview || '(empty)'}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`${item.word_count} words`} size="small" />
                    <Chip label={formatDate(item.reflected_at)} size="small" variant="outlined" />
                  </Stack>

                  {!locked && (
                    <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(item)}
                          aria-label={`Edit ${displayTitle}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(item)}
                          aria-label={`Delete ${displayTitle}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Passcode unlock dialog */}
      <Dialog
        open={unlockDialogOpen}
        onClose={() => {
          setUnlockDialogOpen(false);
          setUnlockPasscodeInput('');
          setUnlockError('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unlock Reflections</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your passcode to view reflection content.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Passcode"
            value={unlockPasscodeInput}
            onChange={(e) => setUnlockPasscodeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
            error={!!unlockError}
            helperText={unlockError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUnlockDialogOpen(false);
              setUnlockPasscodeInput('');
              setUnlockError('');
            }}
            disabled={unlockLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnlock}
            variant="contained"
            disabled={unlockLoading || !unlockPasscodeInput.trim()}
          >
            {unlockLoading ? <CircularProgress size={20} /> : 'Unlock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passcode settings dialog */}
      <Dialog
        open={passcodeSettingsOpen}
        onClose={() => {
          setPasscodeSettingsOpen(false);
          setNewPasscodeInput('');
          setRemovePasscodeInput('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Passcode Settings</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          {!passcodeIsSet ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Set a passcode to protect your reflections. Without it, only timestamps and word counts
                will be visible.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                type="password"
                label="New passcode (min 4 characters)"
                value={newPasscodeInput}
                onChange={(e) => setNewPasscodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSetPasscode()}
                inputProps={{ minLength: 4 }}
              />
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Your reflections are passcode-protected. You can change or remove the passcode below.
              </DialogContentText>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Change passcode
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="New passcode (min 4 characters)"
                value={newPasscodeInput}
                onChange={(e) => setNewPasscodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSetPasscode()}
                inputProps={{ minLength: 4 }}
                sx={{ mb: 3 }}
              />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Remove passcode
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Current passcode"
                value={removePasscodeInput}
                onChange={(e) => setRemovePasscodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRemovePasscode()}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasscodeSettingsOpen(false);
              setNewPasscodeInput('');
              setRemovePasscodeInput('');
            }}
            disabled={passcodeSettingsLoading}
          >
            Cancel
          </Button>
          {passcodeIsSet && removePasscodeInput.trim() && (
            <Button
              onClick={handleRemovePasscode}
              color="error"
              disabled={passcodeSettingsLoading}
            >
              Remove Passcode
            </Button>
          )}
          {newPasscodeInput.length >= 4 && (
            <Button
              onClick={handleSetPasscode}
              variant="contained"
              disabled={passcodeSettingsLoading}
            >
              {passcodeSettingsLoading ? <CircularProgress size={20} /> : passcodeIsSet ? 'Change Passcode' : 'Set Passcode'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Editor dialog — used for both create and edit */}
      <Dialog
        open={editorOpen}
        onClose={() => (saveLoading ? undefined : setEditorOpen(false))}
        fullWidth
        maxWidth="md"
        // On mobile, go full-screen so the textarea gets maximum room.
        fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}
      >
        <DialogTitle>
          {editorMode === 'create' ? 'New Reflection' : 'Edit Reflection'}
        </DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            margin="dense"
            fullWidth
            label="Title (optional)"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            inputProps={{ maxLength: 255 }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            type="datetime-local"
            label="When did this happen?"
            value={reflectedAtDraft}
            onChange={(e) => setReflectedAtDraft(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={10}
            maxRows={30}
            label="What's on your mind?"
            placeholder="Type freely, or tap the microphone on your keyboard and let voice transcription do the work…"
            value={contentDraft}
            onChange={(e) => setContentDraft(e.target.value)}
            // Mobile UX: autocorrect + autocapitalize on, don't spellcheck-nag.
            inputProps={{
              autoCapitalize: 'sentences',
              autoCorrect: 'on',
              spellCheck: 'true',
            }}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Encrypted at rest on the server.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {draftWordCount} {draftWordCount === 1 ? 'word' : 'words'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saveLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saveLoading || !contentDraft.trim()}
          >
            {saveLoading ? (
              <CircularProgress size={20} />
            ) : editorMode === 'create' ? (
              'Save Reflection'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only viewer dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}
      >
        <DialogTitle>
          {viewing?.title?.trim() || 'Untitled reflection'}
          {viewing && (
            <Typography variant="caption" color="text.secondary" display="block">
              {formatDate(viewing.reflected_at)} · {viewing.word_count} words
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={dialogContentPaddingSx}>
          {viewerLoading || !viewing ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography
              variant="body1"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                m: 0,
              }}
            >
              {viewing.content}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!viewing) return;
              setViewerOpen(false);
              openEdit({
                id: viewing.id,
                user_id: viewing.user_id,
                title: viewing.title,
                word_count: viewing.word_count,
                reflected_at: viewing.reflected_at,
                created_at: viewing.created_at,
                updated_at: viewing.updated_at,
              });
            }}
            startIcon={<EditIcon />}
            disabled={!viewing}
          >
            Edit
          </Button>
          <Button onClick={() => setViewerOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Reflection</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Are you sure you want to delete
            {' '}
            <strong>&quot;{deleteTarget?.title?.trim() || 'this reflection'}&quot;</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Reflections;
